/**
 * useCompanyPersonnelQueries — TanStack Query hooks for the company personnel list.
 *
 * Powers CompanyPayroll (All Employees): paginated unified personnel list,
 * summary stats and department filter options. Follows usePettyCashQueries /
 * useCompanyTimesheetQueries conventions (scope, retry: 0, placeholderData).
 */

import { useMemo, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getUnifiedPersonnel,
  getUnifiedPersonnelStats,
  getDepartments,
  getUnifiedPersonnelById,
  getUnifiedAssignmentHistory,
  getLocationHistory,
  getAvailableForAssignment,
  getPersonnel,
  getNextEmployeeCode,
} from '../services/hrApi';
import { getAvailableUsers } from '../services/fieldOperationsApi';
import { api } from '../api';
import { useQueryScope } from '../utils/queryScope';
import {
  usePersonnelRolesQuery,
  invalidatePersonnelRoles,
} from './usePersonnelRolesQueries';

const ROOT = 'company-personnel';

const sharedQueryDefaults = {
  retry: 0,
};

function useScope() {
  return useQueryScope();
}

function buildListParams({
  currentPage,
  itemsPerPage,
  statusFilter,
  typeFilter,
  departmentFilter,
  workContextFilter,
  availableOnlyFilter,
  searchTerm,
}) {
  return {
    skip: (currentPage - 1) * itemsPerPage,
    limit: itemsPerPage,
    status: statusFilter !== 'ALL' ? statusFilter : undefined,
    personnel_type: typeFilter !== 'ALL' ? typeFilter : undefined,
    department_id: departmentFilter || undefined,
    work_context: workContextFilter || undefined,
    available_only: availableOnlyFilter || undefined,
    search: searchTerm || undefined,
  };
}

async function fetchPersonnelList(filters) {
  const params = buildListParams(filters);
  const response = await getUnifiedPersonnel(params);
  const items = Array.isArray(response) ? response : (response.items || []);
  const total = response.total ?? items.length;
  const totalPages = response.total_pages
    ?? (Math.ceil(total / filters.itemsPerPage) || 1);
  return { items, total, totalPages };
}

// ============================================================================
// READ HOOKS
// ============================================================================

export function useCompanyPersonnelList(filters, { enabled = true } = {}) {
  const scope = useScope();
  return useQuery({
    queryKey: [ROOT, scope, 'list', filters],
    queryFn: () => fetchPersonnelList(filters),
    enabled,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
    ...sharedQueryDefaults,
  });
}

/** Company-level employees list with roster month filter (CompanyEmployeesList). */
export function useCompanyEmployeesList(filters, { enabled = true } = {}) {
  const scope = useScope();
  return useQuery({
    queryKey: [ROOT, scope, 'company-employees', filters],
    queryFn: async () => {
      const params = {
        work_context: 'COMPANY',
        status: filters.status || undefined,
        personnel_type: filters.personnelType || undefined,
        department_id: filters.departmentId || undefined,
        position_id: filters.positionId || undefined,
        search: filters.searchTerm || undefined,
        limit: 500,
        roster_year: filters.rosterYear,
        roster_month: filters.rosterMonth,
      };
      return getUnifiedPersonnel(params);
    },
    enabled,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
    ...sharedQueryDefaults,
  });
}

export function useCompanyPersonnelStats({ enabled = true } = {}) {
  const scope = useScope();
  return useQuery({
    queryKey: [ROOT, scope, 'stats'],
    queryFn: getUnifiedPersonnelStats,
    enabled,
    staleTime: 60_000,
    ...sharedQueryDefaults,
  });
}

export function useCompanyPersonnelDepartments({ enabled = true } = {}) {
  const scope = useScope();
  return useQuery({
    queryKey: [ROOT, scope, 'departments'],
    queryFn: async () => {
      const deptData = await getDepartments();
      return deptData || [];
    },
    enabled,
    staleTime: 5 * 60_000,
    ...sharedQueryDefaults,
  });
}

/**
 * Job positions. Thin re-export of the shared cache (usePersonnelRolesQueries) so
 * a position created in Job Positions Management shows up here immediately —
 * previously this key held its own copy and stayed stale for minutes.
 */
export function usePersonnelRoles(options = {}) {
  return usePersonnelRolesQuery(options);
}

export function useActiveProjectsList({ enabled = true } = {}) {
  const scope = useScope();
  return useQuery({
    queryKey: [ROOT, scope, 'active-projects'],
    queryFn: async () => {
      const response = await api.get('/projects');
      return (response.data || []).filter((p) => p.is_active !== false);
    },
    enabled,
    staleTime: 5 * 60_000,
    ...sharedQueryDefaults,
  });
}

export function useNextEmployeeCode(projectId, { enabled = true } = {}) {
  const scope = useScope();
  const id = projectId != null ? parseInt(projectId, 10) : null;
  return useQuery({
    queryKey: [ROOT, scope, 'next-employee-code', id],
    queryFn: async () => {
      const data = await getNextEmployeeCode(id);
      return data?.next_code || '01';
    },
    enabled: enabled && !!id && !Number.isNaN(id),
    staleTime: 60_000,
    ...sharedQueryDefaults,
  });
}

/** Active personnel for assignment-history dropdown. */
export function useAssignmentHistoryPersonnelList({ enabled = true } = {}) {
  const scope = useScope();
  return useQuery({
    queryKey: [ROOT, scope, 'assignment-history-personnel'],
    queryFn: async () => {
      const data = await getUnifiedPersonnel({ status: 'ACTIVE', limit: 500 });
      if (Array.isArray(data)) return data;
      if (data?.items && Array.isArray(data.items)) return data.items;
      return [];
    },
    enabled,
    staleTime: 60_000,
    ...sharedQueryDefaults,
  });
}

export function useLegacyPersonnelDetail(personnelId, { enabled = true } = {}) {
  const scope = useScope();
  const id = personnelId != null ? parseInt(personnelId, 10) : null;
  return useQuery({
    queryKey: [ROOT, scope, 'legacy-detail', id],
    queryFn: () => getPersonnel(id),
    enabled: enabled && !!id && !Number.isNaN(id),
    staleTime: 30_000,
    ...sharedQueryDefaults,
  });
}

export function useCompanyPersonnelDetail(personnelId, { enabled = true } = {}) {
  const scope = useScope();
  const id = personnelId != null ? parseInt(personnelId, 10) : null;
  return useQuery({
    queryKey: [ROOT, scope, 'detail', id],
    queryFn: () => getUnifiedPersonnelById(id),
    enabled: enabled && !!id && !Number.isNaN(id),
    staleTime: 30_000,
    ...sharedQueryDefaults,
  });
}

export function useCompanyPersonnelAssignmentHistory(personnelId, { enabled = true } = {}) {
  const scope = useScope();
  const id = personnelId != null ? parseInt(personnelId, 10) : null;
  return useQuery({
    queryKey: [ROOT, scope, 'assignment-history', id],
    queryFn: () => getUnifiedAssignmentHistory(id),
    enabled: enabled && !!id && !Number.isNaN(id),
    staleTime: 30_000,
    ...sharedQueryDefaults,
  });
}

export function useCompanyPersonnelLocationHistory(personnelId, { enabled = true } = {}) {
  const scope = useScope();
  const id = personnelId != null ? parseInt(personnelId, 10) : null;
  return useQuery({
    queryKey: [ROOT, scope, 'location-history', id],
    queryFn: () => getLocationHistory(id),
    enabled: enabled && !!id && !Number.isNaN(id),
    staleTime: 30_000,
    ...sharedQueryDefaults,
  });
}

export function useLinkableUsers({ enabled = true } = {}) {
  const scope = useScope();
  return useQuery({
    queryKey: [ROOT, scope, 'linkable-users'],
    queryFn: () => getAvailableUsers(),
    enabled,
    staleTime: 60_000,
    ...sharedQueryDefaults,
  });
}

/** Project roster — personnel linked to a project for a calendar month. */
export function useProjectTeamRoster(
  projectId,
  { statusFilter, rosterYear, rosterMonth },
  { enabled = true } = {},
) {
  const scope = useScope();
  const filters = { statusFilter, rosterYear, rosterMonth };
  return useQuery({
    queryKey: [ROOT, scope, 'project-team', projectId, filters],
    queryFn: async () => {
      const params = {
        current_project_id: projectId,
        limit: 500,
        roster_year: rosterYear,
        roster_month: rosterMonth,
      };
      if (statusFilter === 'ALL') {
        params.include_terminated = true;
      } else {
        params.status = statusFilter;
      }
      return getUnifiedPersonnel(params);
    },
    enabled: enabled && !!projectId,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
    ...sharedQueryDefaults,
  });
}

/** Personnel available for project assignment (company level). */
export function useAvailablePersonnelList({ enabled = true } = {}) {
  const scope = useScope();
  return useQuery({
    queryKey: [ROOT, scope, 'available-for-project'],
    queryFn: () => getAvailableForAssignment(),
    enabled,
    staleTime: 30_000,
    ...sharedQueryDefaults,
  });
}

/** Full project record — used to hydrate client name on team list. */
export function useProjectDetail(projectId, { enabled = true } = {}) {
  const scope = useScope();
  return useQuery({
    queryKey: [ROOT, scope, 'project-detail', projectId],
    queryFn: async () => {
      const response = await api.get(`/projects/${projectId}`);
      return response.data;
    },
    enabled: enabled && !!projectId,
    staleTime: 5 * 60_000,
    ...sharedQueryDefaults,
  });
}

/** Assigned project for project-personnel users without an active project in context. */
export function useAssignedProject({ enabled = true } = {}) {
  const scope = useScope();
  return useQuery({
    queryKey: [ROOT, scope, 'assigned-project'],
    queryFn: async () => {
      const { data } = await api.get('/users/me/assigned-project');
      return data?.project_id ? data : null;
    },
    enabled,
    staleTime: 5 * 60_000,
    ...sharedQueryDefaults,
  });
}

/** Fetch full project record into TanStack cache (e.g. quick-activate from nav). */
export function useProjectActivator() {
  const queryClient = useQueryClient();
  const scope = useScope();

  return useCallback(
    async (projectId) => {
      return queryClient.fetchQuery({
        queryKey: [ROOT, scope, 'project-detail', projectId],
        queryFn: async () => {
          const response = await api.get(`/projects/${projectId}`);
          return response.data;
        },
        staleTime: 5 * 60_000,
      });
    },
    [queryClient, scope],
  );
}

// ============================================================================
// INVALIDATION
// ============================================================================

export function useCompanyPersonnelInvalidator() {
  const queryClient = useQueryClient();
  const scope = useScope();

  return useMemo(() => {
    const root = [ROOT, scope];
    const inv = (key) => queryClient.invalidateQueries({ queryKey: key });

    return {
      list: () => inv([...root, 'list']),
      companyEmployees: () => inv([...root, 'company-employees']),
      stats: () => inv([...root, 'stats']),
      projectDetail: (projectId) =>
        projectId != null
          ? inv([...root, 'project-detail', parseInt(projectId, 10)])
          : inv([...root, 'project-detail']),
      detail: (personnelId) =>
        personnelId != null
          ? inv([...root, 'detail', parseInt(personnelId, 10)])
          : inv([...root, 'detail']),
      legacyDetail: (personnelId) =>
        personnelId != null
          ? inv([...root, 'legacy-detail', parseInt(personnelId, 10)])
          : inv([...root, 'legacy-detail']),
      assignmentHistory: (personnelId) =>
        personnelId != null
          ? inv([...root, 'assignment-history', parseInt(personnelId, 10)])
          : inv([...root, 'assignment-history']),
      locationHistory: (personnelId) =>
        personnelId != null
          ? inv([...root, 'location-history', parseInt(personnelId, 10)])
          : inv([...root, 'location-history']),
      refreshAll: () => Promise.all([
        inv([...root, 'list']),
        inv([...root, 'stats']),
        inv([...root, 'company-employees']),
      ]),
      afterPersonnelMutation: (personnelId) => Promise.all([
        inv([...root, 'list']),
        inv([...root, 'stats']),
        inv([...root, 'company-employees']),
        inv([...root, 'project-team']),
        inv([...root, 'available-for-project']),
        personnelId != null ? inv([...root, 'detail', parseInt(personnelId, 10)]) : Promise.resolve(),
        personnelId != null ? inv([...root, 'assignment-history', parseInt(personnelId, 10)]) : Promise.resolve(),
        personnelId != null ? inv([...root, 'location-history', parseInt(personnelId, 10)]) : Promise.resolve(),
      ]),
      projectTeam: (projectId) =>
        projectId != null
          ? inv([...root, 'project-team', projectId])
          : inv([...root, 'project-team']),
      availableForProject: () => inv([...root, 'available-for-project']),
      /** Job positions live in the shared personnel-roles cache, not under this root. */
      roles: () => invalidatePersonnelRoles(queryClient),
      all: () => Promise.all([inv(root), invalidatePersonnelRoles(queryClient)]),
    };
  }, [queryClient, scope]);
}
